import {useQuery} from '@tanstack/react-query'
import {createContext, PropsWithChildren, useContext, useMemo} from 'react'
import {HMComment, UnpackedHypermediaId} from './hm-types'
import {
  CommentsService,
  ListCommentsByReferenceRequest,
  ListCommentsByReferenceResponse,
  ListCommentsResponse,
  ListDiscussionsRequest,
  ListDiscussionsResponse,
} from './models/comments-service'
import {queryKeys} from './models/query-keys'
import {hmId} from './utils/entity-id-url'

type CommentsProviderValue = {
  onReplyClick: (comment: HMComment) => void
  onReplyCountClick: (comment: HMComment) => void
  service: CommentsService | null
}

const defaultCommentsContext = {
  onReplyClick: (comment: HMComment) => {
    console.log('onReplyClick not implemented', comment)
  },
  onReplyCountClick: (comment: HMComment) => {
    console.log('onReplyCountClick not implemented', comment)
  },
  service: null,
}

const CommentsContext = createContext<CommentsProviderValue>(
  defaultCommentsContext,
)

export function CommentsProvider({
  children,
  onReplyClick = defaultCommentsContext.onReplyClick,
  onReplyCountClick = defaultCommentsContext.onReplyCountClick,
  service = null,
}: PropsWithChildren<CommentsProviderValue>) {
  return (
    <CommentsContext.Provider
      value={useMemo(
        () => ({onReplyClick, onReplyCountClick, service}),
        [onReplyClick, onReplyCountClick, service],
      )}
    >
      {children}
    </CommentsContext.Provider>
  )
}

export function useCommentsServiceContext() {
  const context = useContext(CommentsContext)
  if (!context) {
    throw new Error('CommentsContext not found')
  }
  return context
}

export function useCommentsService(params: ListDiscussionsRequest) {
  const context = useCommentsServiceContext()
  return useQuery({
    queryKey: [queryKeys.DOCUMENT_COMMENTS, params.targetId, params.commentId],
    queryFn: async (): Promise<ListCommentsResponse> => {
      if (!context.service) {
        return {comments: [], authors: {}}
      }
      const res = await context.service.listComments(params)
      return res
    },
    enabled: !!context.service,
  })
}

export function useDiscussionsService(params: ListDiscussionsRequest) {
  const context = useCommentsServiceContext()

  return useQuery({
    queryKey: [
      queryKeys.DOCUMENT_DISCUSSION,
      params.targetId,
      params.commentId,
    ],
    queryFn: async (): Promise<ListDiscussionsResponse> => {
      if (!context.service) {
        return {discussions: [], authors: {}}
      }

      const res = await context.service.listDiscussions(params)

      return res
    },
    enabled: !!context.service,
  })
}

export function useBlockDiscussionsService(
  params: ListCommentsByReferenceRequest,
) {
  const context = useCommentsServiceContext()

  return useQuery({
    queryKey: [queryKeys.BLOCK_DISCUSSIONS, params.targetId],
    queryFn: async (): Promise<ListCommentsByReferenceResponse> => {
      if (!context.service) {
        return {comments: [], authors: {}}
      }
      const res = await context.service.listCommentsByReference(params)
      // console.log(
      //   `\n\n======================== ~ useBlockDiscussionsService ~ res:`,
      //   res,
      // )
      return res
    },
    enabled: !!context.service,
  })
}

export function isRouteEqualToCommentTarget({
  id,
  comment,
}: {
  id: UnpackedHypermediaId
  comment: HMComment
}): UnpackedHypermediaId | null {
  if (!id) return null

  const targetRoute = hmId(`${comment.targetAccount}${comment.targetPath}`, {
    version: comment.targetVersion,
  })

  console.log({id, targetRoute})
  console.log('targetRoute.uid == id.uid', targetRoute.uid == id.uid)
  console.log('path', targetRoute.path?.join('/') == id.path?.join('/'))

  if (
    targetRoute.uid == id.uid &&
    targetRoute.path?.join('/') == id.path?.join('/')
  ) {
    return null
  }

  return targetRoute
}
